import { Observable, PropertyChangedEventArgs } from "../../events";
import { TileNavigationState } from "../navigation";
import { ValidableBase } from "../../validable";
import { OrderedCollection } from "../../collections/orderedCollection";
import { TileMapLayerView } from "./tiles.map.layerView";
import { TileView } from "./tiles.map.view";
import { TileNavigationApi } from "../navigation/tiles.navigation.api";
export class TileMapBase extends ValidableBase {
    constructor(display, nav, container) {
        super();
        this._display = null;
        this._navigation = null;
        this._api = null;
        this._layers = container ?? this._buildLayerContainer() ?? this._buildLayerContainerInternal();
        this._layerAddedObserver = this._layers.addedObservable.add(this._onLayerAdded.bind(this));
        this._layerRemovedObserver = this._layers.removedObservable.add(this._onLayerRemoved.bind(this));
        this.display = display ?? null;
        this.navigationState = nav ?? this._buildNavigationState() ?? new TileNavigationState();
        this._view = this._buildView() ?? new TileView();
        this._layerViews = this._buildLayerViewContainer(this._layers) ?? this._buildLayerViewContainerInternal(this._layers);
        this._layerViewAddedObserver = this._layerViews.addedObservable.add(this._onLayerViewAdded.bind(this));
        this._layerViewRemovedObserver = this._layerViews.removedObservable.add(this._onLayerViewRemoved.bind(this));
    }
    get layers() {
        return this._layers;
    }
    get layerViews() {
        return this._layerViews;
    }
    get propertyChangedObservable() {
        if (!this._propertyChangedObservable)
            this._propertyChangedObservable = new Observable();
        return this._propertyChangedObservable;
    }
    get display() {
        return this._display;
    }
    set display(value) {
        this._bindDisplay(value);
    }
    get navigationState() {
        return this._navigation;
    }
    set navigationState(value) {
        this._bindNavigation(value);
    }
    get view() {
        return this._view;
    }
    dispose() {
        super.dispose();
        this._navigationValidableObserver?.disconnect();
        this._displayPropertyObserver?.disconnect();
        this._layerAddedObserver?.disconnect();
        this._layerRemovedObserver?.disconnect();
        this._layerViewAddedObserver?.disconnect();
        this._layerViewRemovedObserver?.disconnect();
    }
    setViewMap(center, zoom, rotation) {
        this._api?.setViewMap(center, zoom, rotation);
        for (const v of this._layerViews) {
            v.navigationApi?.setViewMap(center, zoom, rotation);
        }
        return this;
    }
    zoomMap(delta) {
        this._api?.zoomMap(delta);
        for (const v of this._layerViews) {
            v.navigationApi?.zoomMap(delta);
        }
        return this;
    }
    zoomInMap(delta) {
        this._api?.zoomInMap(delta);
        for (const v of this._layerViews) {
            v.navigationApi?.zoomInMap(delta);
        }
        return this;
    }
    zoomOutMap(delta) {
        this._api?.zoomOutMap(delta);
        for (const v of this._layerViews) {
            v.navigationApi?.zoomOutMap(delta);
        }
        return this;
    }
    translateUnitsMap(tx, ty) {
        this._api?.translateUnitsMap(tx, ty);
        for (const v of this._layerViews) {
            v.navigationApi?.translateUnitsMap(tx, ty);
        }
        return this;
    }
    translateMap(lat, lon) {
        this._api?.translateMap(lat, lon);
        for (const v of this._layerViews) {
            v.navigationApi?.translateMap(lat, lon);
        }
        return this;
    }
    rotateMap(r) {
        this._api?.rotateMap(r);
        for (const v of this._layerViews) {
            v.navigationApi?.rotateMap(r);
        }
        return this;
    }
    get isValid() {
        return super.isValid && this._layers?.isValid && this._layerViews?.isValid;
    }
    _doValidate() {
        this._layers?.validate();
        this._layerViews?.validate();
    }
    _onLayerAdded(eventData, eventstate) {
        const views = eventData.map((l) => this._buildLayerView(l)).filter((i) => i !== null && i !== undefined);
        if (views.length > 0) {
            this._layerViews.add(...views);
            this.invalidate();
        }
    }
    _onLayerRemoved(eventData, eventstate) {
        const toRemove = Array.from(this._layerViews.get((v) => eventData.includes(v.layer)));
        if (toRemove?.length) {
            this._layerViews.remove(...toRemove);
            this.invalidate();
        }
    }
    _onLayerViewAdded(eventData, eventstate) {
        this._updateLayerNavigations(eventData);
    }
    _onLayerViewRemoved(eventData, eventstate) { }
    _updateLayerNavigations(layers) {
        if (layers) {
            const nav = this.navigationState;
            if (nav) {
                for (const layerView of layers) {
                    layerView.navigationState?.copy(nav);
                }
            }
        }
    }
    _onNavigationValidationChanged(event, state) {
        if (event && state.target === this._navigation) {
            this.invalidate();
            this._onNavigationUpdated(state.target);
        }
    }
    _onDisplayPropertyChanged(event, state) {
        switch (event.propertyName) {
            case "size": {
                this.invalidate();
                this._onDisplayResized(event.source);
                break;
            }
            default: {
                break;
            }
        }
    }
    _onNavigationPropertyChanged(event, state) { }
    _bindDisplay(display) {
        if (this._display != display) {
            const old = this.display;
            if (this._display) {
                this._displayPropertyObserver?.disconnect();
                this._displayPropertyObserver = null;
                this._onDisplayUnbinded(this._display);
            }
            this._display = display;
            if (this._display) {
                this._displayPropertyObserver = this._display.propertyChangedObservable?.add(this._onDisplayPropertyChanged.bind(this));
            }
            if (this._layerViews) {
                for (const l of this._layerViews) {
                    l.display = display;
                }
            }
            this.invalidate();
            this._onDisplayBinded(display);
            if (this._propertyChangedObservable && this._propertyChangedObservable.hasObservers()) {
                this._propertyChangedObservable.notifyObservers(new PropertyChangedEventArgs(this, old, this._display, TileMapBase.DISPLAY_PROPERTY_NAME), -1, this, this);
            }
        }
    }
    _bindNavigation(nav) {
        if (this._navigation != nav) {
            const old = this._navigation;
            if (this._navigation) {
                this._navigationValidableObserver?.disconnect();
                this._navigationValidableObserver = null;
                this._navigationPropertyObserver?.disconnect();
                this._navigationPropertyObserver = null;
                this._api?.dispose();
                this._api = null;
                this._onNavigationUnbinded(this._navigation);
            }
            this._navigation = nav;
            if (this._navigation) {
                this._api = new TileNavigationApi(this._navigation);
                this._navigationPropertyObserver = this._navigation.propertyChangedObservable?.add(this._onNavigationPropertyChanged.bind(this));
                this._navigationValidableObserver = this._navigation.validationObservable?.add(this._onNavigationValidationChanged.bind(this));
                this._updateLayerNavigations(this._layerViews);
            }
            this.invalidate();
            this._onNavigationBinded(nav);
            if (this._propertyChangedObservable && this._propertyChangedObservable.hasObservers()) {
                this._propertyChangedObservable.notifyObservers(new PropertyChangedEventArgs(this, old, this._navigation, TileMapBase.NAVIGATION_PROPERTY_NAME), -1, this, this);
            }
        }
    }
    _buildLayerContainer() {
        return this._buildLayerContainerInternal();
    }
    _buildLayerViewContainer(layers) {
        return this._buildLayerViewContainerInternal(layers);
    }
    _buildLayerView(layer) {
        return this._buildLayerViewInternal(layer);
    }
    _onDisplayUnbinded(display) {
    }
    _onDisplayBinded(display) {
    }
    _onNavigationUnbinded(nav) {
    }
    _onNavigationBinded(nav) {
    }
    _onNavigationUpdated(nav) {
    }
    _onDisplayResized(display) {
    }
    _onDisplayTranslated(display) {
    }
    _buildNavigationState() {
        return new TileNavigationState();
    }
    _buildView() {
        return new TileView();
    }
    _buildLayerContainerInternal() {
        return new OrderedCollection();
    }
    _buildLayerViewContainerInternal(layers) {
        return new OrderedCollection(...Array.from(this._layers).map((l) => this._buildLayerView(l) ?? this._buildLayerViewInternal(l)));
    }
    _buildLayerViewInternal(layer) {
        return new TileMapLayerView(layer, this._display, this._view);
    }
}
TileMapBase.DISPLAY_PROPERTY_NAME = "display";
TileMapBase.NAVIGATION_PROPERTY_NAME = "nav";
//# sourceMappingURL=tiles.map.js.map